import { useCallback } from 'react'
import type {
	Control,
	ControllerFieldState,
	ControllerRenderProps,
	FieldPath,
	FieldValues,
	RegisterOptions,
} from 'react-hook-form'
import { Controller } from 'react-hook-form'

import { SelectFile } from '../SelectFile'

interface SelectFileControllerProps<
	TFieldValues extends FieldValues = FieldValues,
> {
	name: FieldPath<TFieldValues>
	control: Control<TFieldValues>
	rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>
	formFieldName?: Parameters<typeof SelectFile>[0]['formFieldName']
	isRequired?: boolean
	label?: string
	showLabel?: boolean
	shouldDirtyOnChange?: boolean
	templateUrl?: string
	onAfterChange?: (value: unknown) => void
	externalError?: string
}

const REQUIRED_RULES = { required: true }

export const SelectFileController = <
	TFieldValues extends FieldValues = FieldValues,
>({
	name,
	control,
	rules,
	formFieldName,
	isRequired,
	label,
	showLabel = false,
	shouldDirtyOnChange,
	templateUrl,
	onAfterChange,
	externalError,
}: SelectFileControllerProps<TFieldValues>) => {
	const render = useCallback(
		({
			field,
			fieldState,
		}: {
			field: ControllerRenderProps<TFieldValues, FieldPath<TFieldValues>>
			fieldState: ControllerFieldState
		}) => (
			<SelectFile
				{...field}
				fieldError={
					fieldState.error ??
					(externalError
						? { message: externalError, type: 'manual' }
						: undefined)
				}
				formFieldName={formFieldName}
				isRequired={isRequired}
				label={label}
				showLabel={showLabel}
				shouldDirtyOnChange={shouldDirtyOnChange}
				templateUrl={templateUrl}
				onAfterChange={onAfterChange}
			/>
		),
		[
			formFieldName,
			isRequired,
			label,
			showLabel,
			shouldDirtyOnChange,
			templateUrl,
			onAfterChange,
			externalError,
		]
	)

	return (
		<Controller
			name={name}
			control={control}
			rules={rules ?? (isRequired ? REQUIRED_RULES : undefined)}
			render={render}
		/>
	)
}
